import {
  Box,
  Button,
  TextInput,
  Grid,
  Group,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { yupResolver } from 'mantine-form-yup-resolver'
import { Database } from 'types/supabase'
import * as yup from 'yup'
import { useCreateAdvertiser, useUpdateAdvertiser } from 'hooks/advertisers'
import { useNavigate } from 'react-router-dom'
import { EditPicture } from 'ui'
import { useState } from 'react'
import { useAppTranslation } from 'hooks/useTranslation'

interface AdvertiserFormProps {
  isNew?: boolean
  advertiser?: Database['public']['Tables']['advertisers']['Row']
  onCreated?: (advertiser: { id: string; name: string }) => void
}

export const AdvertiserForm = (props: AdvertiserFormProps) => {
  const { isNew = false, advertiser, onCreated } = props
  const navigate = useNavigate()
  const [logoFile, setLogoFile] = useState<File>()
  const [imageError, setImageError] = useState(false)
  const { t } = useAppTranslation()

  const createAdvertiser = useCreateAdvertiser((created) => {
    if (onCreated && created && created.id && created.name) {
      onCreated({ id: created.id, name: created.name })
    } else {
      navigate(-1)
    }
  })

  const updateAdvertiser = useUpdateAdvertiser(() => {
    navigate(-1)
  })

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t('advertisers.name_required'))
      .min(2, t('advertisers.name_min_length')),
    category: yup
      .string()
      .required(t('advertisers.category_required'))
      .min(2, t('advertisers.category_min_length')),
    contact_email: yup
      .string()
      .email(t('common.email_invalid'))
      .required(t('advertisers.email_required')),
    contact_phone: yup.string().nullable(),
  })

  const form = useForm({
    initialValues: {
      name: advertiser?.name || '',
      category: advertiser?.category || '',
      contact_email: advertiser?.contact_email || '',
      contact_phone: advertiser?.contact_phone || '',
      whatsapp: advertiser?.whatsapp || '',
      website: advertiser?.website || '',
      instagram: advertiser?.instagram || '',
    },
    validate: yupResolver(schema),
  })

  const formSubmit = async (values: any) => {
    if (imageError) {
      return
    }

    const newAdvertiser = {
      name: values.name,
      category: values.category,
      contact_email: values.contact_email,
      contact_phone: values.contact_phone || undefined,
      whatsapp: values.whatsapp || undefined,
      website: values.website || undefined,
      instagram: values.instagram || undefined,
      logo_file: logoFile,
    }

    if (isNew) {
      try {
        await createAdvertiser.mutateAsync(newAdvertiser)
      } catch (error: any) {
        if (error.code === '23505') {
          form.setErrors({ name: t('advertisers.name_already_exists') })
        } else {
          form.setErrors({ general: error.message })
        }
      }
    } else {
      try {
        await updateAdvertiser.mutateAsync({
          id: advertiser!.id,
          name: values.name,
          category: values.category,
          contact_email: values.contact_email,
          contact_phone: values.contact_phone || undefined,
          whatsapp: values.whatsapp || undefined,
          website: values.website || undefined,
          instagram: values.instagram || undefined,
          currLogoUrl: advertiser!.logo_url,
          newLogoFile: logoFile,
        })
      } catch (error: any) {
        if (error.code === '23505') {
          form.setErrors({ name: t('advertisers.name_already_exists') })
        } else {
          form.setErrors({ general: error.message })
        }
      }
    }
  }

  return (
    <Box>
      <form onSubmit={form.onSubmit(formSubmit)}>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.name')}
              required
              {...form.getInputProps('name')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.category')}
              required
              {...form.getInputProps('category')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.contact_email')}
              type="email"
              required
              {...form.getInputProps('contact_email')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.contact_phone')}
              {...form.getInputProps('contact_phone')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.whatsapp')}
              placeholder={t('advertisers.whatsapp_placeholder')}
              {...form.getInputProps('whatsapp')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.website')}
              placeholder={t('advertisers.website_placeholder')}
              {...form.getInputProps('website')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t('advertisers.instagram')}
              placeholder={t('advertisers.instagram_placeholder')}
              {...form.getInputProps('instagram')}
            />
          </Grid.Col>
          <Grid.Col
            span={4}
            ml="xs"
            style={{ border: '1px grey solid', borderRadius: 4 }}
          >
            <EditPicture
              defaultValue={advertiser?.logo_url}
              onChange={(f) => {
                setLogoFile(f)
                setImageError(false)
              }}
              width={200}
              height={200}
              isError={imageError}
            />
          </Grid.Col>
        </Grid>

        <Group mt="xl" mr="xl" justify="flex-start" w="100%">
          <Button
            type="submit"
            loading={createAdvertiser.isPending || updateAdvertiser.isPending}
            size="md"
          >
            {isNew ? t('common.create') : t('common.save')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            size="md"
          >
            {t('common.cancel')}
          </Button>
        </Group>
      </form>
    </Box>
  )
}